import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SvgIcon } from '../../components/icons';
import { DsRoot } from '../ThemeProvider';
import { AppFrame } from '../shell/AppFrame';
import {
    Button, Card, CardBody, Chip, Column, DataTable, Dialog, KpiCard, Select, useToast,
} from '../primitives';
import {
    Idea, PERSONAS, TYPE_LABEL, formatHours, hoursPerYear, loadIdeas, resetIdeas, saveIdeas,
} from '../data/improvementBoard';
import { ImprovementForm } from './ImprovementForm';
import { ImprovementDetail } from './ImprovementDetail';

/**
 * Improvement Board - a prototype for collecting and ranking staff proposals.
 *
 * Prototype only: no API and no redux. `/design` sits behind the sign-in gate
 * and the "Developers" clearance group, but reads nothing from the signed-in
 * user - the persona switcher stands in for one,
 * and also makes the one-vote-per-employee rule demonstrable, which a single
 * hardcoded identity cannot.
 *
 * State lives in localStorage under its own key (see data/improvementBoard.ts).
 */
export const ImprovementBoard = () => {
    const [ideas, setIdeas] = useState<Idea[]>(loadIdeas);
    const [personaId, setPersonaId] = useState(PERSONAS[0].id);
    const [openIdea, setOpenIdea] = useState<Idea | null>(null);
    const { push } = useToast();

    const persona = PERSONAS.find(p => p.id === personaId) ?? PERSONAS[0];

    const commit = (next: Idea[]) => { setIdeas(next); saveIdeas(next); };

    const toggleVote = (id: string) => {
        commit(ideas.map(idea => idea.id !== id ? idea : {
            ...idea,
            votes: idea.votes.includes(personaId)
                ? idea.votes.filter(v => v !== personaId)
                : [...idea.votes, personaId],
        }));
    };

    const addIdea = (idea: Idea) => {
        commit([idea, ...ideas]);
        push({ title: 'Submitted to the board', body: 'Colleagues can now upvote it.', tone: 'ok' });
    };

    // Pre-sorted by votes: DataTable starts with no sort of its own, so this is
    // the default order, and column clicks still re-sort from here.
    const ranked = useMemo(
        () => [...ideas].sort((a, b) => b.votes.length - a.votes.length || hoursPerYear(b) - hoursPerYear(a)),
        [ideas],
    );

    const totalHours = useMemo(() => ranked.reduce((sum, i) => sum + hoursPerYear(i), 0), [ranked]);

    const columns: Column<Idea>[] = [
        {
            key: 'rank', header: '#', align: 'right', priority: 'low',
            render: (_row, index) => <span className="ds-muted">{index + 1}</span>,
        },
        {
            key: 'title', header: 'Idea', sortable: true,
            render: row => (
                <button
                    type="button"
                    onClick={() => setOpenIdea(row)}
                    style={{ textAlign: 'left', background: 'none', border: 0, padding: 0, cursor: 'pointer', color: 'inherit', font: 'inherit' }}
                >
                    {row.title}
                </button>
            ),
        },
        {
            key: 'type', header: 'Type', sortable: true, priority: 'medium',
            render: row => (
                <Chip tone={row.type === 'bug' ? 'danger' : row.type === 'data' ? 'info' : 'accent'}>
                    {TYPE_LABEL[row.type]}
                </Chip>
            ),
        },
        { key: 'area', header: 'Area', sortable: true, priority: 'low', render: row => row.area },
        {
            key: 'hours', header: 'Est. cost', align: 'right', sortable: true, priority: 'medium',
            render: row => formatHours(hoursPerYear(row)),
        },
        {
            key: 'votes', header: 'Votes', align: 'right', sortable: true,
            render: row => {
                const voted = row.votes.includes(personaId);
                return (
                    <div className="flex items-center justify-end gap-2">
                        <strong>{row.votes.length}</strong>
                        <Button
                            variant={voted ? 'confirm' : 'secondary'}
                            size="sm"
                            onClick={() => toggleVote(row.id)}
                            startIcon={<SvgIcon name={voted ? 'check' : 'add'} size={13} />}
                        >
                            {voted ? 'Voted' : 'Upvote'}
                        </Button>
                    </div>
                );
            },
        },
        { key: 'submittedBy', header: 'From', sortable: true, priority: 'low', render: row => row.submittedBy },
        { key: 'submittedAt', header: 'Date', sortable: true, priority: 'low', render: row => row.submittedAt },
    ];

    return (
        <DsRoot>
            <AppFrame context="Improvement Board">
                <div
                    className="flex flex-wrap items-center gap-2"
                    style={{ marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid var(--ds-border)' }}
                >
                    <Link to="/design" className="ds-btn" data-ds-variant="ghost" data-ds-size="sm">
                        <SvgIcon name="keyboardArrowLeft" size={13} />
                        Directions
                    </Link>
                    <div className="grow" />
                    <Select
                        label="Viewing as"
                        wrapperClassName="min-w-[200px]"
                        options={PERSONAS.map(p => ({ value: p.id, label: `${p.name} — ${p.role}` }))}
                        value={personaId}
                        onChange={e => setPersonaId(e.target.value)}
                    />
                </div>

                <div className="ds-page-header">
                    <div>
                        <div className="ds-eyebrow">Prototype</div>
                        <h1 className="ds-title">Improvement Board</h1>
                        <p className="ds-subtitle">
                            Anything that would make the job easier — a bug worth fixing, a feature worth
                            building, or data from another system worth showing here. The board is ranked by
                            votes, so the ideas your colleagues recognise rise to the top.
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { const seeds = resetIdeas(); setIdeas(seeds); push({ title: 'Demo data reset', tone: 'info' }); }}
                    >
                        Reset demo data
                    </Button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" style={{ marginBottom: 16 }}>
                    <KpiCard label="Ideas on the board" value={String(ranked.length)} />
                    <KpiCard label="Votes cast" value={String(ranked.reduce((s, i) => s + i.votes.length, 0))} />
                    <KpiCard label="Claimed time cost" value={formatHours(totalHours)} />
                    <KpiCard label="Bugs vs ideas" value={`${ranked.filter(i => i.type === 'bug').length} / ${ranked.filter(i => i.type !== 'bug').length}`} />
                </div>

                <Card style={{ marginBottom: 16 }}>
                    <CardBody>
                        <DataTable
                            columns={columns}
                            rows={ranked}
                            getRowKey={row => row.id}
                            pageSize={10}
                            caption="Improvement proposals, ranked by votes"
                            emptyTitle="Nothing on the board yet"
                            emptyHint="Submit the first improvement using the form below."
                        />
                    </CardBody>
                </Card>

                <ImprovementForm author={persona} onSubmit={addIdea} />

                <Dialog
                    open={openIdea !== null}
                    title={openIdea?.title ?? ''}
                    onClose={() => setOpenIdea(null)}
                    width="lg"
                    footer={openIdea && (
                        <div className="flex items-center gap-2">
                            <Button
                                variant={openIdea.votes.includes(personaId) ? 'confirm' : 'primary'}
                                onClick={() => {
                                    toggleVote(openIdea.id);
                                    setOpenIdea(prev => prev && ({
                                        ...prev,
                                        votes: prev.votes.includes(personaId)
                                            ? prev.votes.filter(v => v !== personaId)
                                            : [...prev.votes, personaId],
                                    }));
                                }}
                            >
                                {openIdea.votes.includes(personaId) ? 'Voted' : 'Upvote this'}
                            </Button>
                            <Button variant="ghost" onClick={() => setOpenIdea(null)}>Close</Button>
                        </div>
                    )}
                >
                    {openIdea && <ImprovementDetail idea={openIdea} />}
                </Dialog>

                <footer
                    className="ds-muted"
                    style={{ marginTop: 24, paddingTop: 12, borderTop: '1px solid var(--ds-border)', fontSize: 'var(--ds-fs-xs)' }}
                >
                    Prototype with dummy data, stored in this browser only. Switch persona above to see how
                    one-vote-per-employee behaves.
                </footer>
            </AppFrame>
        </DsRoot>
    );
};

export default ImprovementBoard;
